import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import * as React from "react";

import { EntityComments } from "@/shared/components/comments";
import {
  RobotoStyledTab,
  RobotoStyledTabs,
  RobotoTabPanel,
} from "@/shared/components/index.tsx";
import { TagList } from "@/shared/components/TagList";
import { CollectionRecord } from "@/shared/domain/collections";

import { CollectionAudit } from "./CollectionAudit.tsx";
import styles from "./CollectionDetails.module.css";
import { CollectionDetailsTab } from "./CollectionDetailsTab.tsx";
import { CollectionItemsTab } from "./CollectionItemsTab.tsx";
import { CollectionPermissionDetails } from "./CollectionPermissionDetails.tsx";
import { CollectionTypeChip } from "./CollectionTypeChip.tsx";
import { EditableCollectionDescription } from "./EditableCollectionDescription.tsx";
import { EditableCollectionName } from "./EditableCollectionName.tsx";

interface CollectionDetailsProps {
  collection: CollectionRecord;
}

const TABS = { ITEMS: 0, DETAILS: 1, ACTIVITY: 2, PERMISSIONS: 3 } as const;

function totalItemCount(resources: CollectionRecord["resources"]): number {
  return Object.values(resources).reduce((sum, arr) => sum + arr.length, 0);
}

export const CollectionDetails: React.FC<CollectionDetailsProps> = ({
  collection,
}) => {
  const [activeTab, setActiveTab] = React.useState<number>(TABS.ITEMS);

  const itemCount = totalItemCount(collection.resources);

  return (
    <div className={styles.root}>
      {/* ── Header + Tab bar ── */}
      <div className={styles.headerSection}>
        <div className={styles.header}>
          {/* Left: name / description / metadata / tags */}
          <div className={styles.headerLeft}>
            <div className={styles.titleRow}>
              <FolderOutlinedIcon
                className={styles.folderIcon}
                fontSize="small"
              />
              <EditableCollectionName
                name={collection.name ?? ""}
                collectionId={collection.collection_id}
                orgId={collection.org_id}
              />
              <CollectionTypeChip resourceType={collection.resource_type} />
            </div>

            <EditableCollectionDescription
              description={collection.description ?? ""}
              collectionId={collection.collection_id}
              orgId={collection.org_id}
            />

            <div className={styles.metadataRow}>
              <PersonOutlinedIcon className={styles.metadataIcon} />
              <span className={styles.metadataLabel}>Created By:</span>
              <span className={styles.metadataText}>
                {collection.created_by}
              </span>
              <span className={styles.metadataDot}>·</span>
              <CalendarTodayOutlinedIcon className={styles.metadataIcon} />
              <span className={styles.metadataLabel}>Created:</span>
              <span className={styles.metadataText}>
                {new Date(collection.created).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
              {itemCount > 0 && (
                <>
                  <span className={styles.metadataDot}>·</span>
                  <span className={styles.metadataText}>
                    {itemCount} {itemCount === 1 ? "item" : "items"}
                  </span>
                </>
              )}
            </div>

            {collection.tags.length > 0 && (
              <div className={styles.tagsRow}>
                <TagList tags={collection.tags} />
              </div>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className={styles.tabBar}>
          <RobotoStyledTabs
            value={activeTab}
            onChange={(_e, v: number) => setActiveTab(v)}
          >
            <RobotoStyledTab
              label="Items"
              icon={
                <FormatListBulletedOutlinedIcon style={{ fontSize: "1rem" }} />
              }
              iconPosition="start"
            />
            <RobotoStyledTab
              label="Details"
              icon={<InfoOutlinedIcon style={{ fontSize: "1rem" }} />}
              iconPosition="start"
            />
            <RobotoStyledTab
              label="Activity"
              icon={<AccessTimeOutlinedIcon style={{ fontSize: "1rem" }} />}
              iconPosition="start"
            />
            <RobotoStyledTab
              label="Permissions"
              icon={<ShieldOutlinedIcon style={{ fontSize: "1rem" }} />}
              iconPosition="start"
            />
          </RobotoStyledTabs>
        </div>
      </div>

      <RobotoTabPanel
        value={activeTab}
        index={TABS.ITEMS}
        className={
          activeTab === TABS.ITEMS ? styles.itemsTabPanelBox : undefined
        }
        innerBoxClassName={
          activeTab === TABS.ITEMS ? styles.itemsTabPanelInner : undefined
        }
      >
        <div className={styles.itemsTabPanel}>
          <CollectionItemsTab collection={collection} />
        </div>
      </RobotoTabPanel>

      <RobotoTabPanel value={activeTab} index={TABS.DETAILS}>
        <div className={styles.tabPanel}>
          <CollectionDetailsTab collection={collection} />
        </div>
      </RobotoTabPanel>

      <RobotoTabPanel value={activeTab} index={TABS.ACTIVITY}>
        <div className={styles.tabPanel}>
          <CollectionAudit collection={collection} />
        </div>
      </RobotoTabPanel>

      <RobotoTabPanel value={activeTab} index={TABS.PERMISSIONS}>
        <div className={styles.tabPanel}>
          <CollectionPermissionDetails
            collectionId={collection.collection_id}
          />
        </div>
      </RobotoTabPanel>

      <div className={styles.commentsSection}>
        <EntityComments
          entityType="collection"
          entityId={collection.collection_id}
        />
      </div>
    </div>
  );
};
